import * as React from 'react';
import Box from '@mui/material/Box';
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import syntiro_logo_horizontal_white_text from '../../assets/syntiro-logo-horizontal-white-text.svg';
import ep_logo_white from '../../assets/ep-logo-white.webp';
import aeeAward2026 from '../../assets/landing_page/AEE_AWARD_2026.svg';
import saee_award_2025 from '../../assets/landing_page/SAEE_AWARD_2025.svg';
import sanea_award from '../../assets/landing_page/SANEA_AWARD.svg';
import PrimaryNavBar from '../shared/nav/PrimaryNavBar';
import { IconButton, Menu, Theme, useTheme } from '@mui/material';
import { CaptionText } from '../shared/typography/PrimaryTypography';

interface NavBarWebLink {
  label: string;
  uri: string;
}

/**
 * A static bar at the top of the app used to navigate through the landing page.
 * @param {void} handleDisplayLoginModal Set the display state of the login client modal.
 * @returns {JSX.Element}
 */
const NavBarWeb = () => {
  const [mobileNavAnchor, setMobileNavAnchor] = React.useState<null | HTMLElement>(null);
  const navigate: NavigateFunction = useNavigate();
  const location: Location = useLocation();
  const theme: Theme = useTheme();

  const is_landing_page: boolean = location.pathname === '/';

  const links: Array<NavBarWebLink> = [
    { label: 'Home', uri: '/#home' },
    { label: 'Solutions', uri: '/#solutions' },
    { label: 'Resources', uri: '/#resources' },
    { label: 'FAQs', uri: '/faq' },
    { label: 'Contact Us', uri: '/contact' }
  ];

  const handleHomeNavigation = (): void => {
    navigate('/');
  };

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setMobileNavAnchor(event.currentTarget);
  };

  const handleCloseNavMenu = (): void => {
    setMobileNavAnchor(null);
  };

  return (
    <PrimaryNavBar position="static">
      <Box sx={{ width: '100%', px: { xs: 2, lg: 12 } }}>
        <Box
          sx={{
            width: '100%',
            py: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {/* Left side - Navigation links, Syntiro logo, and mobile menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 } }}>
            {/* Mobile Menu Icon - show on left for mobile */}
            <IconButton
              size="large"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              sx={{ display: { xs: 'block', md: 'none' }, color: 'white' }}
            >
              <MenuIcon />
            </IconButton>

            {/* Syntiro Logo - only show when not on landing page */}
            {!is_landing_page && (
              <img
                src={syntiro_logo_horizontal_white_text}
                alt="Syntiro"
                onClick={handleHomeNavigation}
                style={{ width: '100%', maxWidth: 200, cursor: 'pointer' }}
              />
            )}

            {/* Desktop Navigation Links */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3 }}>
              {links.map((link: NavBarWebLink) => (
                <a key={link.label} href={link.uri} style={{ textDecoration: 'none' }}>
                  <CaptionText weight="Medium" sx={{ display: 'inline', color: '#e6e6e6' }} dark>
                    {link.label}
                  </CaptionText>
                </a>
              ))}
            </Box>
          </Box>

          {/* Right side - Awards and EP Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Award Logos - Hidden on mobile */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              <img src={aeeAward2026} alt="AEE Award 2026" style={{ height: 75, width: 'auto' }} />
              <img
                src={saee_award_2025}
                alt="SAEE Award 2025"
                style={{ height: 75, width: 'auto' }}
              />
              <img src={sanea_award} alt="SANEA Award" style={{ height: 75, width: 'auto' }} />
            </Box>

            {/* EP Logo */}
            <a href="https://energypartners.co.za/" target="_blank" rel="noopener noreferrer">
              <img
                src={ep_logo_white}
                alt="Energy Partners"
                style={{ width: '100%', maxWidth: 150, cursor: 'pointer' }}
              />
            </a>
          </Box>

          {/* Mobile Menu */}
          <Menu
            id="menu-appbar"
            anchorEl={mobileNavAnchor}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            open={Boolean(mobileNavAnchor)}
            onClose={handleCloseNavMenu}
            sx={{ display: { xs: 'block', md: 'none' } }}
          >
            {links.map((link: NavBarWebLink) => (
              <a key={link.label} href={link.uri} style={{ textDecoration: 'none' }}>
                <CaptionText
                  weight="Medium"
                  sx={{ display: 'block', px: 2, py: 1, color: theme.palette.primary.main }}
                  dark
                  onClick={handleCloseNavMenu}
                >
                  {link.label}
                </CaptionText>
              </a>
            ))}
          </Menu>
        </Box>
      </Box>
    </PrimaryNavBar>
  );
};

export default NavBarWeb;
